/**
 * Misc UI state (Zustand).
 */
import { create } from "zustand";

interface Toast {
  id: string;
  variant: "default" | "success" | "warning" | "destructive";
  title: string;
  description?: string;
}

interface UIState {
  theme: "light" | "dark";
  sidebarOpen: boolean;
  /** PrimarySidebar collapsed/expanded (P2.1-S1). Default expanded. */
  primarySidebarCollapsed: boolean;
  contextPanelOpen: boolean;
  toasts: Toast[];
  setTheme: (theme: "light" | "dark") => void;
  toggleTheme: () => void;
  setSidebar: (open: boolean) => void;
  setPrimarySidebarCollapsed: (collapsed: boolean) => void;
  togglePrimarySidebar: () => void;
  setContextPanel: (open: boolean) => void;
  pushToast: (toast: Omit<Toast, "id">) => void;
  dismissToast: (id: string) => void;
}

const detectInitialTheme = (): "light" | "dark" => {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const detectInitialSidebarCollapsed = (): boolean => {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("primarySidebarCollapsed");
  return stored === "1";
};

export const useUIStore = create<UIState>((set) => ({
  theme: detectInitialTheme(),
  sidebarOpen: true,
  primarySidebarCollapsed: detectInitialSidebarCollapsed(),
  contextPanelOpen: true,
  toasts: [],
  setTheme: (theme) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("theme", theme);
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
    set({ theme });
  },
  toggleTheme: () =>
    set((s) => {
      const next = s.theme === "dark" ? "light" : "dark";
      if (typeof window !== "undefined") {
        window.localStorage.setItem("theme", next);
        document.documentElement.classList.toggle("dark", next === "dark");
      }
      return { theme: next };
    }),
  setSidebar: (open) => set({ sidebarOpen: open }),
  setPrimarySidebarCollapsed: (collapsed) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        "primarySidebarCollapsed",
        collapsed ? "1" : "0",
      );
    }
    set({ primarySidebarCollapsed: collapsed });
  },
  togglePrimarySidebar: () =>
    set((s) => {
      const next = !s.primarySidebarCollapsed;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          "primarySidebarCollapsed",
          next ? "1" : "0",
        );
      }
      return { primarySidebarCollapsed: next };
    }),
  setContextPanel: (open) => set({ contextPanelOpen: open }),
  pushToast: (toast) =>
    set((s) => ({
      toasts: [
        ...s.toasts,
        { ...toast, id: `toast_${Date.now()}_${Math.random().toString(36).slice(2)}` },
      ],
    })),
  dismissToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
